/** Pure Promotions list/inspect display mapping — no I/O. D4D. */

import { CTA_TYPE_LABELS, isEligibleForPublicApi, type CtaType } from "@vital/shared";
import type { StatusTone } from "./overview-ops";
import {
  CONTENT_STATE_LABELS,
  FILTER_ALL,
  FILTER_PAUSED,
  FILTER_QUEUE,
} from "./content-state";

export type PromotionInventoryRow = {
  id: string;
  state: string;
  title: string;
  body: string;
  badge: string;
  ctaType: CtaType | string | null;
  ctaLabel: string;
  ctaHref: string | null;
  imagePath: string | null;
  imageAlt: string;
  priority: number;
  scope: string;
  siteCode: string | null;
  siteName: string | null;
  groupId: string | null;
  groupName: string | null;
  startsAt: string | null;
  endsAt: string | null;
  submittedAt: string | null;
  createdAt?: string;
  updatedAt?: string;
  rejectionReason: string | null;
  pausedFromState: string | null;
};

export type PromotionSortKey = "updated" | "title" | "cta";

export type EligibilityDisplay = {
  label: string;
  tone: StatusTone;
  detail?: string;
};

export type AssetStateDisplay = {
  label: string;
  tone: StatusTone;
};

export function promotionStateLabelFa(state: string): string {
  return CONTENT_STATE_LABELS[state] ?? state;
}

export function promotionCtaLabelFa(
  ctaType: string | null | undefined,
  ctaLabel?: string | null,
): string {
  if (!ctaType) return "بدون CTA";
  const typeLabel =
    (CTA_TYPE_LABELS as Record<string, string>)[ctaType] ?? ctaType;
  const label = ctaLabel?.trim();
  return label ? `${typeLabel} — ${label}` : typeLabel;
}

export function promotionCtaTone(ctaType: string | null | undefined): StatusTone {
  return ctaType ? "info" : "neutral";
}

export function promotionImageState(
  imagePath: string | null | undefined,
): AssetStateDisplay {
  if (imagePath?.trim()) {
    return { label: "تصویر دارد", tone: "success" };
  }
  return { label: "بدون تصویر", tone: "neutral" };
}

/**
 * Honest eligibility from lifecycle + existing schedule helper only.
 */
export function promotionEligibility(
  row: Pick<PromotionInventoryRow, "state" | "startsAt" | "endsAt">,
  now: Date = new Date(),
): EligibilityDisplay {
  if (row.state === "draft" || row.state === "pending_approval") {
    return { label: "منتشر نشده", tone: "neutral", detail: promotionStateLabelFa(row.state) };
  }
  if (row.state === "paused") {
    return { label: "متوقف — بدون نمایش عمومی", tone: "warning" };
  }
  if (row.state === "archived") {
    return { label: "بایگانی — بدون نمایش عمومی", tone: "neutral" };
  }
  if (row.state === "expired") {
    return { label: "منقضی — بدون نمایش عمومی", tone: "neutral" };
  }
  if (row.state === "published" || row.state === "scheduled") {
    const starts = row.startsAt ? new Date(row.startsAt) : null;
    const ends = row.endsAt ? new Date(row.endsAt) : null;
    const ok = isEligibleForPublicApi(starts, ends, now);
    if (ok) {
      return {
        label: "واجد شرایط نمایش عمومی",
        tone: "success",
        detail: row.state === "scheduled" ? "زمان‌بندی‌شده و داخل بازه" : "منتشرشده و داخل بازه",
      };
    }
    return {
      label: "خارج از پنجرهٔ زمانی",
      tone: "warning",
      detail: promotionStateLabelFa(row.state),
    };
  }
  return { label: "نامشخص", tone: "neutral", detail: row.state };
}

export function matchesPromotionQuery(row: PromotionInventoryRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const hay = [
    row.title,
    row.body,
    row.badge,
    row.state,
    promotionStateLabelFa(row.state),
    row.ctaType ?? "",
    promotionCtaLabelFa(row.ctaType, row.ctaLabel),
    row.ctaHref ?? "",
    row.siteCode ?? "",
    row.siteName ?? "",
    row.groupName ?? "",
    row.scope,
    row.imagePath ?? "",
  ]
    .join(" ")
    .toLowerCase();
  return hay.includes(q);
}

export function matchesPromotionListFilter(
  row: PromotionInventoryRow,
  listFilter: string,
): boolean {
  if (listFilter === FILTER_ALL) return true;
  return row.state === listFilter;
}

export function filterAndSortPromotions(
  rows: PromotionInventoryRow[],
  opts: { query: string; listFilter: string; sort: PromotionSortKey },
): PromotionInventoryRow[] {
  const filtered = rows.filter(
    (row) =>
      matchesPromotionListFilter(row, opts.listFilter) &&
      matchesPromotionQuery(row, opts.query),
  );
  const sorted = [...filtered];
  sorted.sort((a, b) => {
    if (opts.sort === "title") {
      return a.title.localeCompare(b.title, "fa", { sensitivity: "base" });
    }
    if (opts.sort === "cta") {
      const ac = a.ctaType ?? "";
      const bc = b.ctaType ?? "";
      return ac.localeCompare(bc, "en");
    }
    const au = a.updatedAt ?? a.submittedAt ?? a.createdAt ?? "";
    const bu = b.updatedAt ?? b.submittedAt ?? b.createdAt ?? "";
    return bu.localeCompare(au);
  });
  return sorted;
}

export type RelatedPromotionLink = { id: string; label: string; to: string };

export function relatedPromotionLinks(): RelatedPromotionLink[] {
  return [
    { id: "sites-preview", label: "پیش‌نمایش مؤثر (سایت‌ها)", to: "/sites#effective-preview" },
    { id: "announcements", label: "اطلاعیه‌ها", to: "/announcements" },
    { id: "sponsored", label: "تبلیغات ویژه", to: "/sponsored" },
    { id: "settings-cta", label: "فهرست CTA", to: "/settings" },
    { id: "audit", label: "سابقه تغییرات", to: "/audit" },
  ];
}

export { FILTER_ALL, FILTER_QUEUE, FILTER_PAUSED };

/** Preset badge labels for promotion offer cards (plain text, ≤ badgeMax). */
export const PROMOTION_BADGE_PRESETS = [
  "جدید",
  "ویژه",
  "محدود",
  "رایگان",
  "تخفیف",
  "پیشنهاد ویژه",
  "فقط امروز",
  "آخر هفته",
  "بسته ویژه",
  "هدیه",
] as const;

export type PromotionBadgePreset = (typeof PROMOTION_BADGE_PRESETS)[number];

/** Preset list plus legacy value when editing an older draft. */
export function promotionBadgeOptions(current?: string | null): string[] {
  const trimmed = current?.trim() ?? "";
  if (!trimmed) return [...PROMOTION_BADGE_PRESETS];
  if ((PROMOTION_BADGE_PRESETS as readonly string[]).includes(trimmed)) {
    return [...PROMOTION_BADGE_PRESETS];
  }
  return [trimmed, ...PROMOTION_BADGE_PRESETS];
}

export function isPromotionBadgePreset(value: string): value is PromotionBadgePreset {
  return (PROMOTION_BADGE_PRESETS as readonly string[]).includes(value);
}
